/** @jsx jsx */
import React from 'react'
import { jsx } from 'theme-ui'
import { css } from '@emotion/react'
import { isFinite, get } from 'lodash'

import Button from '../../button'
import { RatioBox } from '../../image'
import Card from '../../card'
import { ImageInline } from '../../image'
import Paragraph from '../../paragraph'
import VideoWrapper from '../../video-wrapper'

import { floodBgImage, scrollX } from '../../../styles/css'
import { FlexCols, FlexCol, VStack } from '../../../styles/els'
import { formatImage, renderHTML, resolveButtons } from '../../../lib/helpers'

import TickSVG from '../../../styles/icons/tick-dark.svg'

const Cell = (props) => {
  const {
    width,
    tier,
    text,
    note,
    tick,
    button,
    isLabel,
    isHighlight,
    isBlank,
  } = props

  return (
    <div
      sx={{
        variant: tier === 1 ? 'text.bold_28' : 'text.regular_19',
        textAlign: isLabel ? 'left' : 'center',
        width: isFinite(width) ? `${width}%` : null,
        backgroundColor: isBlank
          ? null
          : isLabel
          ? 'b1'
          : isHighlight
          ? 'b8'
          : 'b6',
        color: isLabel ? 'b6' : 'dark',
        px: 4,
        pt: '4px',
        pb: '5px',
        borderRightWidth: isBlank ? null : '2px',
        borderRightStyle: isBlank ? null : 'solid',
        borderRightColor: isBlank ? null : 'b0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',

        '&:last-of-type': {
          borderRightWidth: '0px',
        },
      }}
    >
      {tick ? (
        <div>
          <RatioBox
            ratio={1}
            sx={{
              width: '30px',
              backgroundImage: `url(${TickSVG})`,
              mx: 'auto',
            }}
            css={css`
              ${floodBgImage}
            `}
          />
        </div>
      ) : null}
      {text ? <div>{text}</div> : null}
      {note ? (
        <div sx={{ variant: 'text.regular_13', pt: text ? '3px' : null }}>
          {note}
        </div>
      ) : null}
      {button ? (
        <Button {...button} color={isHighlight ? 'light' : 'b5'} />
      ) : null}
    </div>
  )
}

const Row = (props) => {
  const { isBlank, children, ...otherProps } = props

  return (
    <div
      sx={{
        display: 'flex',
        justifyContent: 'stretch',
        alignItems: 'stretch',
        borderBottomWidth: isBlank ? null : '2px',
        borderBottomStyle: isBlank ? null : 'solid',
        borderBottomColor: isBlank ? null : 'b0',
        minHeight: '60px',

        '&:last-of-type': {
          borderBottomWidth: '0px',
        },
      }}
      {...otherProps}
    >
      {children}
    </div>
  )
}

const Table = (props) => {
  const { tableHeader, tableRows } = props
  const maxColumns = tableHeader && tableHeader.length ? tableHeader.length : 0
  const width = 100 / (maxColumns + 1)

  return (
    <div
      css={css`
        ${scrollX}
      `}
    >
      <div
        sx={{
          minWidth: '720px',
        }}
      >
        <div
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {tableHeader && tableHeader.length > 0 ? (
            <Row>
              <Cell width={width} isLabel={true} />
              {tableHeader.map((_cell, _i) => {
                return (
                  <Cell
                    key={_i}
                    width={width}
                    text={_cell.text}
                    tier={1}
                    isHighlight={_cell.highlight}
                  />
                )
              })}
            </Row>
          ) : null}
          {tableRows && tableRows.length > 0
            ? tableRows.map((_row, _i) => {
                return (
                  <Row key={_i}>
                    <Cell width={width} isLabel={true} text={_row.rowLabel} />
                    {_row.cells && _row.cells.length > 0
                      ? _row.cells.map((_cell, _j) => {
                          if (_j > maxColumns) {
                            return null
                          }

                          const _highlight = get(tableHeader, `${_j}.highlight`)
                          return (
                            <Cell
                              key={_j}
                              width={width}
                              text={_cell.text}
                              note={_cell.note}
                              tick={_cell.showTick}
                              isHighlight={_highlight}
                            />
                          )
                        })
                      : null}
                  </Row>
                )
              })
            : null}
        </div>
        {tableHeader && tableHeader.length > 0 ? (
          <Row
            sx={{
              pt: 4,
            }}
          >
            <Cell width={width} isLabel={true} isBlank={true} />
            {tableHeader.map((_cell, _i) => {
              return (
                <Cell
                  key={_i}
                  width={width}
                  isBlank={true}
                  isHighlight={_cell.highlight}
                  button={{
                    label: 'Try for free',
                    type: 'try',
                  }}
                />
              )
            })}
          </Row>
        ) : null}
      </div>
    </div>
  )
}

const PricingTable = (props) => {
  const { table, notes, label, ...otherProps } = props

  return (
    <div
      sx={{
        py: 7,
      }}
    >
      {label ? (
        <div
          sx={{
            variant: `textBlock.default`,
            maxWidth: '960px',
            color: null,
            textAlign: 'left',
            pb: 5,
          }}
        >
          {<p>{label}</p>}
        </div>
      ) : null}
      {table ? <Table {...table} /> : null}
      {notes && notes.length > 0 ? (
        <VStack
          space={2}
          sx={{
            variant: 'text.regular_13',
            color: 'b5',
            mx: 'auto',
            pt: table ? 8 : null,
          }}
        >
          {notes.map((_note, _i) => {
            const { note, text } = _note
            return (
              <div key={_i}>
                {note ? (
                  <span
                    sx={{
                      variant: 'text.bold_13',
                      pr: 4,
                      minWidth: '100px',
                      display: 'inline-block',
                    }}
                  >
                    {renderHTML(note)}
                  </span>
                ) : null}
                {text ? renderHTML(text) : null}
              </div>
            )
          })}
        </VStack>
      ) : null}
    </div>
  )
}

export default PricingTable
